import { useMemo } from 'react';

import Location, { LocationDetail } from 'dashboard/components/Location';
import { MapMarker } from 'dashboard/components/Map';
import { stringifyLatitude, stringifyLongitude } from 'dashboard/lib/utils/stringFormat';
import { CaseObject } from 'dashboard/models/case';
import { InquirySession } from 'dashboard/models/inquiry-session';
import { SUPPORTED_REPORT_TYPES } from 'dashboard/models/report';
import { ReportProfile } from 'dashboard/models/report/profile';
import {
  Verification,
  VerificationDatabase,
  VerificationGovernmentId,
  VerificationPhoneNumber,
} from 'dashboard/models/verification';
import { ModuleLocation } from 'dashboard/modules/Location';
import Placeholder from 'dashboard/modules/Placeholder';
import useCurrentEnvironmentType from 'lib/hooks/useCurrentEnvironmentType';
import { ApiModel } from 'lib/interfaces/api';
import { assertNever } from 'lib/utils/types';
import { VerificationStatus } from 'lib/verification';

interface LocationModuleProps {
  module: ModuleLocation;
  isRedacted: boolean;
  caseObjects: CaseObject[];
  sessions: InquirySession[];
  verifications: Verification[];
  isLoading: boolean;
}

const LocationModule = ({
  module,
  isRedacted,
  caseObjects,
  sessions,
  verifications,
  isLoading,
}: LocationModuleProps) => {
  const reports = caseObjects.filter((caseObject) => caseObject.type in SUPPORTED_REPORT_TYPES);
  const { type: environmentType } = useCurrentEnvironmentType();

  /**
   * Filter all our data inputs to the ones containing location data.
   */
  const locations = useMemo<LocationDetail[]>(
    () =>
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      [
        ...reports,
        ...sessions,
        ...verifications.filter((v) =>
          [
            VerificationStatus.Passed,
            VerificationStatus.RequiresRetry,
            VerificationStatus.Failed,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          ].includes(v.attributes.status),
        ),
      ]
        .filter(
          (entity) =>
            entity.type === ApiModel.VerificationGovernmentId ||
            entity.type === ApiModel.VerificationDatabase ||
            entity.type === ApiModel.InquirySession ||
            entity.type === ApiModel.ReportProfile,
        )
        .map(
          (
            entity: Parameters<typeof formatEntityAsLocationDetail>[0],
          ): LocationDetail | undefined => {
            const l = formatEntityAsLocationDetail(entity);

            // Pending objects sometimes have undefined lat/lng; don't display these.
            return l.lat != null && l.lng != null && l.location.length > 0 ? l : undefined;
          },
        )
        .filter(Boolean),
    [reports, sessions, verifications],
  );

  if (isLoading) {
    return <Placeholder title='Location' loading={true} />;
  }

  return (
    <div data-test='module__location'>
      <Location
        isRedacted={isRedacted}
        locations={locations}
        environmentType={environmentType}
        customLocations={module['custom-locations']}
      />
    </div>
  );
};

export default LocationModule;

function formatBasicEntityAsLocationDetail(
  entity: VerificationPhoneNumber | VerificationGovernmentId | VerificationDatabase | ReportProfile,
): LocationDetail {
  const {
    'address-street-1': addressStreet1,
    'address-street-2': addressStreet2,
    'address-city': addressCity,
    'address-subdivision': addressSubdivision,
    'address-postal-code': addressPostalCode,
    'country-code': countryCode,
    'location-accuracy': locationAccuracy,
    'country-mismatch': countryMismatch,
  } = entity.attributes;

  const cityAndSubdivision = [addressCity, addressSubdivision].filter(Boolean).join(', ');
  const cityAndSubdivisionAndPostalCode = [cityAndSubdivision, addressPostalCode]
    .filter(Boolean)
    .join(' ');

  // Leave this as 0 for now because we don't have a good place to link to
  // for these list matches.
  const listMatchCount = 0;

  return {
    id: entity.id,
    type: entity.type,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    location: [addressStreet1, addressStreet2, cityAndSubdivisionAndPostalCode].filter(Boolean),
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    country: countryCode,
    listMatchCount: listMatchCount,
    locationAccuracy: locationAccuracy,
    countryMismatch: countryMismatch,
    ...MapMarker.fromBasicGeocodedEntity(entity),
  };
}

function formatSessionAsLocationDetail(
  session: InquirySession,
  config?: { useGps: boolean },
): LocationDetail {
  const {
    latitude,
    longitude,
    'region-name': regionName,
    'country-code': countryCode,
  } = session.attributes;

  // Leave this as 0 for now because we don't have a good place to link to
  // for these list matches.
  const listMatchCount = 0;

  let formattedCoordinates = undefined;
  if (latitude != null && longitude != null) {
    formattedCoordinates = `${stringifyLatitude(latitude)} ${stringifyLongitude(longitude)}`;
  }

  return {
    id: session.id,
    type: session.type,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    location: [formattedCoordinates, regionName].filter(Boolean),
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    country: countryCode,
    listMatchCount: listMatchCount,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    locationAccuracy: null,
    countryMismatch: false,
    ...MapMarker.fromInquirySession(session, { useGps: !!config?.useGps }),
  };
}

function formatEntityAsLocationDetail(
  entity:
    | ReportProfile
    | InquirySession
    | VerificationPhoneNumber
    | VerificationGovernmentId
    | VerificationDatabase,
): LocationDetail {
  switch (entity.type) {
    // Basic
    case ApiModel.VerificationPhoneNumber:
    case ApiModel.VerificationDatabase:
    case ApiModel.ReportProfile:
      return formatBasicEntityAsLocationDetail(entity);
    // Documents
    case ApiModel.VerificationGovernmentId:
      return {
        ...formatBasicEntityAsLocationDetail(entity),
        startDate: entity.attributes['issue-date'],
        endDate: entity.attributes['expiration-date'],
      };
    // Network -- will be deprecated
    case ApiModel.InquirySession:
      return formatSessionAsLocationDetail(entity);
    default:
      assertNever(entity);
  }
}
